/* eslint-disable react/no-unescaped-entities */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Clock, Sparkles, HeadphonesIcon, Heart,
  Plane, Users, Building2, ShieldCheck, Umbrella, Mountain,
  Globe2, HandHeart, MessageCircleHeart
} from "lucide-react";

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const AboutPage = () => {
  return (
    <div data-testid="about-page">

      {/* ============== SECTION 1: HERO ============== */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/27981933/pexels-photo-27981933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Scenic travel destination"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        </div>
        <div className="container-custom relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-red-400 font-medium text-sm tracking-widest uppercase mb-6">About Dnotours</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              We Don't Just Plan Trips.<br />
              <span className="text-red-400">We Plan Memories.</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-xl">
              Dnotours has been turning travel dreams into real journeys for Indian families, couples, and groups for over 8 years.
            </p>
            <Link to="/get-quote">
              <Button className="bg-red-500 hover:bg-red-600 text-white h-12 px-8 rounded-full text-base font-medium" data-testid="hero-plan-trip-btn">
                Plan My Trip
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============== SECTION 2: OUR STORY ============== */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <span className="text-red-500 font-medium text-sm tracking-widest uppercase mb-4 block">Our Story</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-8 leading-tight">
                A Decade of Crafting Unforgettable Journeys
              </h2>
              <div className="space-y-5 text-slate-600 text-base leading-relaxed">
                <p>
                  Dnotours started with one simple belief — travel should feel easy, exciting, and personal. Not stressful.
                </p>
                <p>
                  For more than 8 years, we have helped thousands of Indian travelers explore the world with confidence. From a couple planning their dream honeymoon, to a big joint family planning their first international holiday, to a company organizing a reward trip for their best employees — we have done it all, and we have done it with care.
                </p>
                <p>
                  We are based in Mumbai, but our work takes us across the globe. Whether it's the beaches of Bali, the snow of Switzerland, the deserts of Dubai, or a spiritual journey through Chardham — we plan every trip like it's our own family going.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1767519633523-eafa83d20507?w=800&q=80"
                alt="Happy family on vacation"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-500 text-white rounded-2xl px-6 py-4 shadow-xl">
                <div className="text-3xl font-bold">8+</div>
                <div className="text-white/80 text-sm">Years of Trust</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============== SECTION 3: FOUNDER'S MESSAGE ============== */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="founder-section">
        <div className="container-custom">
          <motion.div {...fadeIn} className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-red-500 font-medium text-sm tracking-widest uppercase mb-4 block">From Our Founder</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
                A Message From Our Founder
              </h2>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 relative">
              <div className="absolute top-6 left-8 text-red-100 text-8xl font-serif leading-none select-none">"</div>
              <div className="relative z-10 space-y-5 text-slate-600 text-base leading-relaxed italic">
                <p>
                  I started Dnotours with a simple idea — every traveler deserves a trip that feels stress-free, well planned, and truly special.
                </p>
                <p>
                  I have seen travel from every side. I have sat with families planning their first big trip and seen the worry on their faces about getting it right. I have spoken to couples who just wanted their honeymoon to be perfect, without having to chase ten different people for ten different bookings. That is exactly the problem I wanted to solve.
                </p>
                <p>
                  Over the last 8+ years, I have built Dnotours on three simple promises — honesty in every price we quote, care in every itinerary we design, and support at every step of the journey, not just before you book.
                </p>
                <p>
                  Today, our team plans everything from small family holidays to large group tours, corporate events, and pilgrimage trips like Chardham Yatra. But no matter how big we grow, our promise stays the same — we treat every traveler like family.
                </p>
                <p>
                  Thank you for trusting us with your travel dreams. I personally make sure that trust is never taken lightly.
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md">
                  M
                </div>
                <div>
                  <div className="font-bold text-slate-900 text-lg">Mihir Vipul Babaria</div>
                  <div className="text-slate-500 text-sm">Founder & Director, Dnotours</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============== SECTION 4: WHY CHOOSE US ============== */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose-section">
        <div className="container-custom">
          <motion.div {...fadeIn} className="text-center mb-16">
            <span className="text-red-500 font-medium text-sm tracking-widest uppercase mb-4 block">Why Us</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
              Why Travelers Choose Dnotours
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Clock,
                title: "8+ Years of Trust",
                desc: "Thousands of happy travelers and repeat clients across India.",
              },
              {
                icon: Sparkles,
                title: "Truly Customized Trips",
                desc: "No copy-paste itineraries. Every plan is built around you.",
              },
              {
                icon: HeadphonesIcon,
                title: "End-to-End Support",
                desc: "Flights, hotels, visas, insurance — we handle it all under one roof.",
              },
              {
                icon: Heart,
                title: "Real People, Real Care",
                desc: "A dedicated team that's just a call or message away, anytime you need us.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center p-6 rounded-2xl border border-slate-100 hover:border-red-100 hover:shadow-lg transition-all duration-300 group"
                data-testid={`why-card-${i}`}
              >
                <div className="w-14 h-14 mx-auto bg-red-50 group-hover:bg-red-100 rounded-xl flex items-center justify-center mb-5 transition-colors">
                  <item.icon className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SECTION 5: WHAT WE DO ============== */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="container-custom">
          <motion.div {...fadeIn} className="text-center mb-16">
            <span className="text-red-500 font-medium text-sm tracking-widest uppercase mb-4 block">Our Services</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
              What We Help You With
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Heart, label: "Honeymoon Packages" },
              { icon: Users, label: "Family & Group Holidays" },
              { icon: Building2, label: "Corporate Travel & MICE Events" },
              { icon: ShieldCheck, label: "Visa Assistance" },
              { icon: Umbrella, label: "Travel Insurance" },
              { icon: Mountain, label: "Pilgrimage Tours (Chardham & more)" },
              { icon: Globe2, label: "Customized International Packages" },
            ].map((service, i) => (
              <motion.div
                key={service.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:border-red-200 transition-colors"
                data-testid={`service-${i}`}
              >
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                  <service.icon className="h-5 w-5 text-red-500" />
                </div>
                <span className="font-medium text-slate-700 text-sm">{service.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SECTION 6: OUR PROMISE ============== */}
      <section className="py-20 md:py-28 bg-white" data-testid="values-section">
        <div className="container-custom">
          <motion.div {...fadeIn} className="text-center mb-16">
            <span className="text-red-500 font-medium text-sm tracking-widest uppercase mb-4 block">Our Promise</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
              What We Stand For
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: HandHeart,
                title: "Honesty First",
                desc: "No hidden costs. No false promises. Just clear, honest planning.",
              },
              {
                icon: Sparkles,
                title: "Care in Every Detail",
                desc: "From your first enquiry to your last day of travel, we plan with you in mind.",
              },
              {
                icon: MessageCircleHeart,
                title: "Support That Doesn't End",
                desc: "We are with you before, during, and after your trip — not just until you pay.",
              },
            ].map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-center"
                data-testid={`value-${i}`}
              >
                <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center mb-6">
                  <value.icon className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="font-bold text-slate-900 text-xl mb-3 font-heading">{value.title}</h3>
                <p className="text-slate-500 leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SECTION 7: CTA ============== */}
      <section className="py-20 bg-red-500" data-testid="about-cta">
        <div className="container-custom">
          <motion.div {...fadeIn} className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Ready to Plan Your Next Trip?
            </h2>
            <p className="text-lg text-white/90 mb-10">
              Let's turn your travel plans into a journey you'll never forget.
            </p>
            <Link to="/get-quote">
              <Button
                className="bg-white text-red-500 hover:bg-white/90 h-14 px-10 rounded-full text-base font-bold shadow-xl"
                data-testid="cta-talk-expert-btn"
              >
                Talk to Our Travel Expert
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
